// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { alpha } from '@mui/material/styles';
import { awBorderGrey, awMutedGrey, awPageGrey } from 'Utils/themes';
import { PAGE_TOOLBAR_HEIGHT_PX } from '../PageToolbar/styles';
import { CONTENT_COLUMN_SIDE_PADDING } from '../styles';

const styles = (theme: any) => ({
  // The dashboard pins the controls row under the fixed toolbar while the projects scroll, so
  // the sort and view controls never need scrolling back up past the folders to reach. A flex
  // column so the row's own top margin stays inside the band and is painted over. Above the
  // rows at rest, below a row or card being dragged up to a folder. Pinned a pixel under the
  // toolbar's edge rather than flush with it: a fixed box and a sticky box round to device
  // pixels independently at fractional zoom, and a flush pair can leave a hairline between them
  // through which a card's top edge shows. The toolbar paints over the overlap. Sideways it
  // spans the content column's side padding as well as the column: a card's shadow reaches past
  // its box into that padding, and a band only as wide as the column would let the shadows of
  // the cards passing beneath show beside it. The inner padding keeps the controls on the column.
  stickyBand: {
    position: 'sticky',
    top: `${PAGE_TOOLBAR_HEIGHT_PX - 1}px`,
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: awPageGrey,
    marginLeft: theme.spacing(-CONTENT_COLUMN_SIDE_PADDING),
    marginRight: theme.spacing(-CONTENT_COLUMN_SIDE_PADDING),
    paddingLeft: theme.spacing(CONTENT_COLUMN_SIDE_PADDING),
    paddingRight: theme.spacing(CONTENT_COLUMN_SIDE_PADDING),
    paddingBottom: theme.spacing(1),
  },
  subheader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
    // On a phone the controls wrap under the title
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
      rowGap: theme.spacing(1),
    },
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    letterSpacing: '0.6857px',
    color: theme.palette.text.primary,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '26px',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(2),
    },
  },
  dropdown: {
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.5143px',
    color: awMutedGrey,
    '& .MuiInputBase-input': {
      color: awMutedGrey,
      textAlign: 'right',
    },
  },
  menuItem: {
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.5571px',
    color: '#484d52',
    minHeight: '35px',
  },
  // The menu the design draws: square-ish, the shared light shadow, and a muted band on the
  // hovered or current option. Without explicit anchoring (below) MUI lays a standard-variant
  // Select's menu over the control instead of under it.
  menuPaper: {
    borderRadius: '1px',
    boxShadow: theme.lightShadow,
    marginTop: '4px',
    '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: alpha(awMutedGrey, 0.1),
    },
  },
  // Figma: a hairline between the dropdowns and the list/cards switch.
  divider: {
    width: '1px',
    height: '26px',
    flexShrink: 0,
    backgroundColor: theme.palette.borderGrey,
  },
  viewSwitch: {
    display: 'flex',
    alignItems: 'center',
    height: '38px',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${awBorderGrey}`,
    borderRadius: '4px',
    padding: '2px',
    gap: '2px',
  },
  viewSwitchButton: {
    width: '34px',
    height: '32px',
    borderRadius: '4px',
    color: awMutedGrey,
  },
  viewSwitchButtonActive: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
});

export default styles;
